/**
 * Wire schemas for `c_acLoyaltyData`, the Annex Cloud loyalty state SFRA saves
 * on the customer profile (`int_annexcloud_sfra_v3/.../loyaltyHelper.js` on
 * origin/develop).
 *
 * Annex persists numeric fields as STRINGS and SFRA `Number()`-wraps every
 * read (`loyaltyHelper.js:199,364`), so numbers are coerced, never type-checked.
 */
import { z } from "zod";

import { zJson } from "~/lib/zod-helpers";

/**
 * The balance a failed Annex read leaves behind. SFRA's `getPointsAndDetail`
 * seeds every field with '' and `updateLoyaltyDetails` saves the result whether
 * or not the read answered (`loyaltyHelper.js:37-41, 416-430`), from login,
 * order confirmation, `/loyalty-perks`, enroll and the profile job. Annex itself
 * always answers a number (`0` for a new member, probed 2026-10-17), so '' only
 * ever means "unknown". Every reader coerces it to 0 (`Number('')`), which is
 * why it must be checked explicitly.
 */
export const UNKNOWN_POINTS = "";

/**
 * One schema per site id, built on first use: the blob is parsed several times
 * per page (server and browser), and building a Zod schema costs more than
 * the parse. Site ids are a fixed handful, so the cache stays tiny.
 */
function memoBySite<T>(build: (siteId: string) => T): (siteId: string) => T {
  const cache = new Map<string, T>();
  return siteId => {
    let schema = cache.get(siteId);
    if (schema === undefined) {
      schema = build(siteId);
      cache.set(siteId, schema);
    }
    return schema;
  };
}

/** The parsed blob with this site's key holding a scope object. */
export const keyedLoyaltySchema = memoBySite((siteId: string) =>
  z.object({ [siteId]: z.record(z.unknown()) }).passthrough(),
);

/**
 * The blob in either of the two formats SFRA reads, tagged by which one it is:
 * - `keyed`: `{ [siteId]: scope, ... }`, what SFRA writes
 *   (`addLoyaltyDetailsProfile`, `loyaltyHelper.js:393`). Wins when present.
 * - `flat`: one scope at the top level (legacy single-site shape), which SFRA
 *   reads when the site key is missing (`loyaltyHelper.js:198-203`). Also
 *   chosen when the site key holds no object, where SFRA would read the broken
 *   key instead; `logBrokenLoyaltyScope` (root loader) logs that case once.
 * `scope` is a fresh copy inside `blob`, so patching it patches `blob`.
 */
export const siteLoyaltySchema = memoBySite((siteId: string) =>
  z.preprocess(
    zJson,
    z.union([
      keyedLoyaltySchema(siteId).transform(blob => ({
        format: "keyed" as const,
        blob,
        scope: blob[siteId],
      })),
      z
        .object({ optInStatus: z.string() })
        .passthrough()
        .transform(blob => ({
          format: "flat" as const,
          blob,
          scope: blob,
        })),
    ]),
  ),
);

/**
 * A saved number: a finite number, or a string that is entirely one (Annex
 * persists "250"). Anything else ('', null, " ", [], booleans) is unread, never
 * the 0 or 1 `Number()` would make of it.
 */
export const loyaltyNumberSchema = z.union([
  z.number().finite(),
  z
    .string()
    .trim()
    .regex(/^-?\d+(\.\d+)?$/)
    .transform(Number),
]);

/** One redeemable reward, as SFRA's `loyaltyHelper.getRewards()` saves it. */
export const rewardSchema = z.object({
  rewardId: z.string(),
  /** Raw display string from Annex (may be bare "15" or "$15 OFF"; varies). */
  displayText: z
    .unknown()
    .transform(value => (typeof value === "string" ? value : "")),
  /** Points cost to redeem. */
  creditRequired: loyaltyNumberSchema,
  /** Dollar value. Used both as the apply `amount` and as the display source for the label. */
  rewardCurrency: loyaltyNumberSchema,
});

/**
 * One site's scope. Only a non-string `optInStatus` rejects it; a reward with
 * an unreadable amount is dropped on its own and a bad tier reads as none. The
 * balance exists only when `pointsKnown`, so it cannot be read unchecked.
 */
export const loyaltyScopeSchema = z
  .object({
    optInStatus: z.string(),
    availablePoints: z.unknown(),
    rewards: z.unknown(),
    currentTier: z.unknown(),
  })
  .transform(scope => {
    const points = loyaltyNumberSchema.safeParse(scope.availablePoints);
    return {
      /** SFRA writes "YES" / "NO"; read it through `isOptedIn`. */
      optInStatus: scope.optInStatus,
      // No balance was read ('' after a failed Annex read, missing, garbage):
      // render "updating", never "0 points".
      ...(points.success
        ? { pointsKnown: true as const, availablePoints: points.data }
        : { pointsKnown: false as const }),
      ...(Array.isArray(scope.rewards) && {
        rewards: scope.rewards.flatMap(raw => {
          const reward = rewardSchema.safeParse(raw);
          return reward.success ? [reward.data] : [];
        }),
      }),
      ...(typeof scope.currentTier === "string" && {
        currentTier: scope.currentTier,
      }),
    };
  });
